import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutDashboard, Plug, Award, ArrowRight, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { cardCertificationApi, cardEngineApi } from '../../services/iso8583Api';
import { PageHeader, Card, cardLink, CardEndpointNote } from './common';

/**
 * Where the card corridor stands for this participant: is the card link up,
 * how far certification has got, and what to do next.
 */
export default function CardDashboard() {
  const [health, setHealth] = useState(null);
  const [healthError, setHealthError] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoaded, setReportLoaded] = useState(false);

  useEffect(() => {
    cardEngineApi.health().then(setHealth).catch(() => setHealthError(true));
    cardCertificationApi.progress().then(setReport).catch(() => setReport(null))
      .finally(() => setReportLoaded(true));
  }, []);

  const up = health?.status === 'UP';
  const link = cardLink(health);
  const sc = report?.scorecard;
  const next = !report ? null
    : (report.cases || []).find((c) => c.obligation === 'MANDATORY' && (c.status === 'NOT_RUN' || c.status === 'FAIL'));

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader icon={LayoutDashboard} title="ISO 8583 card corridor">
        Integrate with a card switch — Postilion, SmartVista, Base24 — and certify your gateway as acquirer and issuer.
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Card link" icon={Plug}>
          <div className="flex items-center gap-2">
            {up ? <CheckCircle className="w-5 h-5 text-green-500" />
              : health || healthError ? <XCircle className="w-5 h-5 text-red-500" />
              : <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />}
            <span className="text-sm font-medium text-gray-900">
              {up ? 'Listening' : healthError ? 'Unreachable' : health ? 'Down' : 'Checking…'}
            </span>
          </div>
          {link.published
            ? <p className="font-mono text-sm text-gray-700 mt-2">{link.host}:{link.port}{link.tls ? ' (TLS)' : ''}</p>
            : <p className="text-sm text-amber-800 mt-2">Endpoint not published by this Sandbox</p>}
          <CardEndpointNote endpoint={link} className="mt-2" />
          <p className="text-xs text-gray-500 mt-1">
            {health ? `${health.dialect} · ${health.framing} framing`
              : healthError ? 'The card engine did not answer.' : ''}
          </p>
          <Link to="/iso8583/getting-started" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline mt-3">
            How to connect <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </Card>

        <Card title="Certification" icon={Award}>
          {sc ? (
            <>
              <p className="text-sm text-gray-900">
                <span className="text-2xl font-bold">{sc.mandatory.passed}/{sc.mandatory.total}</span>
                <span className="text-gray-500"> mandatory cases passed · verdict </span>
                <span className="font-medium">{String(report.status).replace('_', ' ')}</span>
              </p>
              {next && (
                <p className="text-xs text-gray-500 mt-2">
                  Next: <span className="font-mono">{next.caseId}</span> — {next.title}
                </p>
              )}
            </>
          ) : <p className="text-sm text-gray-500">
                {reportLoaded ? 'No evidence yet — sign on and send a transaction.' : 'Loading…'}
              </p>}
          <Link to="/iso8583/certification" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline mt-3">
            Open certification <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </Card>
      </div>

      <Card title="Suggested path">
        <ol className="list-decimal ml-5 space-y-1.5 text-sm text-gray-700">
          <li><Link to="/iso8583/getting-started" className="text-primary-600 hover:underline">Connect and sign on</Link> with the institution id AfricaNenda enrolled for you.</li>
          <li>Send purchases with <Link to="/iso8583/test-cards" className="text-primary-600 hover:underline">test cards</Link> — including the lost-reply amounts, and recover from them.</li>
          <li>On <Link to="/iso8583/certification" className="text-primary-600 hover:underline">Certification</Link>, send yourself the issuer-side cases and answer them.</li>
          <li>Check what crossed the link in the <Link to="/iso8583/ledger" className="text-primary-600 hover:underline">Message Ledger</Link>, then finalize.</li>
        </ol>
      </Card>
    </div>
  );
}
